import { Injectable, inject } from '@angular/core';
import { NgbCalendar, NgbDate, NgbDatepicker, NgbDatepickerI18n } from '@ng-bootstrap/ng-bootstrap';
import { DateConversionService } from './date-conversion.service';
import { FormControlDirection } from '../datepicker-range-advanced.interfaces';
import { SelectOption } from '@rx-applications/shared/utils';

@Injectable({ providedIn: 'root' })
export class DateNavigationService {
  ngbCalendarService = inject(NgbCalendar);
  ngbI18nService = inject(NgbDatepickerI18n);
  dateConversionService = inject(DateConversionService);

  getMonthsList(): SelectOption[] {
    const monthsArray = Array.from({ length: 12 }, (_, i) => i + 1);
    return monthsArray.map((month, index) => {
      return { label: this.ngbI18nService.getMonthFullName(index + 1), value: month, disabled: false };
    });
  }

  getYearsList(minDate: Date, maxDate: Date): SelectOption[] {
    const minYear = minDate.getFullYear();
    const maxYear = maxDate.getFullYear();
    const listOfYears = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);

    return listOfYears.map((year) => {
      return { label: year.toString(), value: year, disabled: false };
    });
  }

  navigate(datePicker: NgbDatepicker, direction: FormControlDirection): NgbDate[] {
    const { state, calendar } = datePicker;
    const isLeftDirection = direction === 'left';

    const newLeftDate: NgbDate = isLeftDirection
      ? calendar.getPrev(state.firstDate, 'm')
      : calendar.getNext(state.firstDate, 'm');
    const newRightDate: NgbDate = isLeftDirection
      ? calendar.getPrev(state.lastDate, 'm')
      : calendar.getNext(state.lastDate, 'm');

    datePicker.navigateTo(newLeftDate);
    return [newLeftDate, newRightDate];
  }

  monthChange(datePicker: NgbDatepicker, month: number, direction: FormControlDirection): NgbDate[] {
    const { state, calendar } = datePicker;
    const isLeftDirection = direction === 'left';

    let newRightDate: NgbDate = isLeftDirection
      ? calendar.getNext(new NgbDate(state.firstDate.year, month, 1), 'm')
      : new NgbDate(state.lastDate.year, month, 1);

    let newLeftDate: NgbDate = isLeftDirection
      ? new NgbDate(state.firstDate.year, month, 1)
      : calendar.getPrev(newRightDate, 'm');

    [newLeftDate, newRightDate] = this.getValidDates(datePicker, newLeftDate, newRightDate);

    datePicker.navigateTo(newLeftDate);
    return [newLeftDate, newRightDate];
  }

  yearChange(datePicker: NgbDatepicker, year: number, direction: FormControlDirection): NgbDate[] {
    const { state, calendar} = datePicker;
    const isLeftDirection = direction === 'left';

    let newRightDate: NgbDate = isLeftDirection
      ? calendar.getNext(new NgbDate(year, state.firstDate.month, 1), 'm')
      : new NgbDate(year, state.lastDate.month, 1);
    let newLeftDate: NgbDate = isLeftDirection
      ? new NgbDate(year, state.firstDate.month, 1)
      : calendar.getPrev(newRightDate, 'm');

    [newLeftDate, newRightDate] = this.getValidDates(datePicker, newLeftDate, newRightDate);

    datePicker.navigateTo(newLeftDate);
    return [newLeftDate, newRightDate];
  }

  private getValidDates(datePicker: NgbDatepicker, newLeftDate: NgbDate, newRightDate: NgbDate): NgbDate[] {
    const { calendar, minDate, maxDate } = datePicker;

    if (minDate && newLeftDate.before(minDate)) {
      newLeftDate = NgbDate.from(minDate);
      newRightDate = calendar.getNext(NgbDate.from(minDate), 'm');
    } else if (maxDate && newRightDate.after(maxDate)) {
      newRightDate = NgbDate.from(maxDate);
      newLeftDate = calendar.getPrev(NgbDate.from(maxDate), 'm');
    }

    return [newLeftDate, newRightDate];
  }

  isArrowEnabled(datePicker: NgbDatepicker, direction: FormControlDirection): boolean {
    if (!datePicker) return false;
    const { state, calendar } = datePicker;
    if (!state.firstDate) return false;
    const dateReference = direction === 'left' ? state.firstDate : state.lastDate;
    const newDate = direction === 'left' ? calendar.getPrev(dateReference, 'm') : calendar.getNext(dateReference, 'm');
    const rightBoundary = calendar.getNext(state.maxDate, 'm');
    const leftBoundary = calendar.getPrev(state.minDate, 'm');
    return newDate.before(rightBoundary) && newDate.after(leftBoundary);
  }

  getEnabledMonths(
    monthsList: SelectOption[],
    selectedYear: number,
    minDate: Date,
    maxDate: Date,
  ): SelectOption[] {
    if (!monthsList?.length) {
      return [];
    }
    return monthsList.map((option) => {
      const currentDate = new NgbDate(selectedYear, +option.value, 1);
      const isDisabled = this.isDateDisabled(currentDate, minDate, maxDate);
      return { ...option, disabled: isDisabled };
    });
  }

  private isDateDisabled(
    currentDate: NgbDate,
    minDate: Date,
    maxDate: Date,
  ): boolean {
    const isAfterMaxDate = currentDate.after(this.dateConversionService.toNgbDate(maxDate));
    const isBeforeMinDate = currentDate.before(this.dateConversionService.toNgbDate(minDate));

    return isAfterMaxDate || isBeforeMinDate;
  }
}
